/**
 * URL Debugger utility to capture OAuth redirect errors
 */

export const captureUrlParams = () => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const fragment = new URLSearchParams(url.hash.slice(1));

  const allParams = {
    url: window.location.href,
    search: Object.fromEntries(params.entries()),
    hash: Object.fromEntries(fragment.entries()),
    pathname: url.pathname,
  };

  // Only log if there are parameters or errors to debug
  const hasParams = params.toString() || fragment.toString();
  const isDev = import.meta.env.DEV;

  if (!hasParams && !isDev) {
    return allParams; // Silent return for normal navigation
  }

  if (isDev && hasParams) {
    // eslint-disable-next-line no-console
    console.group('🔍 URL Debug Information');
    // eslint-disable-next-line no-console
    console.log('Full URL:', window.location.href);
    // eslint-disable-next-line no-console
    console.log('Search params:', Object.fromEntries(params.entries()));
    // eslint-disable-next-line no-console
    console.log('Hash params:', Object.fromEntries(fragment.entries()));
    // eslint-disable-next-line no-console
    console.log('Pathname:', url.pathname);
  }

  // Check for common OAuth error parameters
  const errorParams = ['error', 'error_description', 'error_uri', 'state'];
  const foundErrors = errorParams.filter(
    (param) => params.has(param) || fragment.has(param)
  );

  if (foundErrors.length > 0) {
    // eslint-disable-next-line no-console
    console.error('🚨 OAuth Errors Found:');
    foundErrors.forEach((param) => {
      const value = params.get(param) || fragment.get(param);
      // eslint-disable-next-line no-console
      console.error(`${param}:`, value);
    });
    if (isDev && hasParams) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  } else if (foundErrors.length === 0) {
    // Check for OAuth success parameters
    const successParams = ['code', 'access_token', 'id_token'];
    const foundSuccess = successParams.filter(
      (param) => params.has(param) || fragment.has(param)
    );

    if (foundSuccess.length > 0) {
      // eslint-disable-next-line no-console
      console.log('✅ OAuth Success Parameters:');
      foundSuccess.forEach((param) => {
        const value = params.get(param) || fragment.get(param);
        // eslint-disable-next-line no-console
        console.log(
          `${param}:`,
          value ? `${value.substring(0, 20)}...` : value
        );
      });
    }

    if (isDev && hasParams) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  return allParams;
};

export const setupUrlWatcher = () => {
  const isDev = import.meta.env.DEV;

  // Capture initial URL only if in dev mode or has parameters
  // const initialParams = captureUrlParams();
  if (
    isDev &&
    (new URL(window.location.href).search || new URL(window.location.href).hash)
  ) {
    // eslint-disable-next-line no-console
    console.log('🎯 Initial page load URL capture with parameters');
  }

  // Watch for URL changes (for SPAs) - only if in dev mode
  if (isDev) {
    let lastUrl = window.location.href;

    const observer = new MutationObserver(() => {
      if (lastUrl !== window.location.href) {
        lastUrl = window.location.href;
        const url = new URL(window.location.href);
        if (url.search || url.hash) {
          // eslint-disable-next-line no-console
          console.log('🔄 URL changed with parameters:');
          captureUrlParams();
        }
      }
    });

    observer.observe(document, {
      subtree: true,
      childList: true,
    });

    // Also listen for popstate events
    window.addEventListener('popstate', () => {
      const url = new URL(window.location.href);
      if (url.search || url.hash) {
        // eslint-disable-next-line no-console
        console.log('📍 Popstate event with parameters:');
        captureUrlParams();
      }
    });

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', captureUrlParams);
    };
  }

  return () => {}; // No-op for production
};
